import {
	ApplePayButton,
	ButtonType,
	Edges,
	Environment,
	Locale,
	Scope,
	SupportedNetworks,
	ThemeMode
} from '@tap-payments/apple-pay-button';

import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import useAlert from 'hooks/useAlert';
import useLocale from 'hooks/useLocale';

import { setPaymentResult } from 'pages/Topbar/ChargeAccount/ChargeAccountSlice';

import axios from 'services/axios';
import { PAYMENT } from 'services/consts';

import { setLoading } from 'stores/layoutSlice';
import { setUserDetails } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';
import { PAYMENT_GATEWAY_TYPE } from 'utils/constants';

import getTapPaymentUserPhone from './getTapPaymentUserPhone';

let popup;

const TapApplePayButton = ({ isSubscription = false, nextStep }) => {
	const { fullName, email, localization } = useSelector((state) => state?.user);
	const formMethods = useFormContext();
	const { paymentGatewayType } = localization;
	const { LangCountryCode, Currency } = useLocale();
	const { darkMode } = useSelector((state) => state?.layout);
	const { showError, showSuccess } = useAlert();
	const dispatch = useDispatch();
	const { watch, setValue } = formMethods;

	const userPhone = useMemo(() => getTapPaymentUserPhone(), []);
	const codWallet = watch('codWallet');
	const total = watch('total');
	const promoCode = watch('promoCode');

	const chargeCODWallet = async () => {
		if (codWallet && !watch('isCodPaymentSuccess')) {
			try {
				const { data } = await axios.post(PAYMENT.MAKE_CHARGE_COD_WALLET, {
					amount: codWallet,
					...(total === codWallet && { code: promoCode })
				});
				dispatch(
					setUserDetails({
						CODRemainingAmount: data?.remainingCODAmount
					})
				);
				setValue('isCodPaymentSuccess', true);
				if (total === codWallet) {
					nextStep();
					return;
				}
				showSuccess({
					header: 'success',
					message: 'success.codWallet'
				});
				return data;
			} catch (error) {
				showError(error || error?.response?.data?.message);
				setValue('isCodPaymentSuccess', false);
			}
		}
	};

	const checkPayment = async (id) =>
		axios
			.post(PAYMENT.CHECK_PAYMENT, { ID: id })
			.then((res) => {
				const { data, status } = res;
				if (status === 200 && data) {
					if (data.status !== 'pending') {
						dispatch(setPaymentResult(data));
						dispatch(setLoading(false));
						nextStep();
					} else {
						setTimeout(() => {
							checkPayment(id);
						}, 3 * 1000);
					}
				} else {
					showError();
					AppLogger.error(res);
				}
			})
			.catch((error) => {
				showError();
				AppLogger.error(error);
				dispatch(setLoading(false));
			});

	useEffect(() => {
		const handleMessage = (message) => {
			if (message.data.event !== 'checkPaymentResult') return;

			popup.close();

			setTimeout(() => {
				checkPayment(message.data?.paymentID);
			}, 5 * 1000); // wait for 5 second to make sure the webhook is triggered
		};

		window.addEventListener('message', handleMessage);

		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, []);

	const completeCreditPayment = async (token) => {
		dispatch(setLoading(true));

		await axios
			.post(PAYMENT.MAKE_CHARGE, {
				subtotal: watch('subTotal'),
				code: watch('promoCode'),
				countryCode: userPhone.countryCode,
				phone: userPhone.phone,
				...(codWallet && { codWalletAmount: codWallet }),
				token
			})
			.then(async (res) => {
				const { data, status } = res;
				if (status === 200 && data) {
					await chargeCODWallet();
					dispatch(setPaymentResult(data));
					nextStep();
					dispatch(setLoading(false));
				} else {
					showError();
					AppLogger.error(res);
					dispatch(setLoading(false));
				}
			})
			.catch((error) => {
				if (error.response?.data.code === 'error.P002') {
					showError({
						message: 'errorMsg.invalidPromoCode'
					});
				} else if (error.response?.data.code === 'error.P014') {
					showError({
						message: 'errorMsg.tapPaymentConnectionTimedOut'
					});
				} else {
					showError();
					AppLogger.error(error);
				}
				dispatch(setLoading(false));
			});
	};

	return (
		<div
			style={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				height: '100%',
				margin: 'auto',
				maxWidth: '24rem',
				padding: '3rem'
			}}>
			<ApplePayButton
				// The public Key provided by Tap
				publicKey={
					paymentGatewayType === PAYMENT_GATEWAY_TYPE.TAP_PAYMENT_UAE
						? import.meta.env.VITE_TAP_PAYMENT_UAE_APPLE_PAY_KEY
						: import.meta.env.VITE_TAP_PAYMENT_APPLE_PAY_KEY
				}
				// The environment of the SDK and it can be one of these environments
				environment={
					import.meta.env.VITE_ENVIRONMENT === 'production' ||
					import.meta.env.VITE_ENVIRONMENT === 'prerelease'
						? Environment.Production
						: Environment.Development
				}
				merchant={{
					//  The merchant domain name
					domain: 'app.tryoto.com',
					//  The merchant identifier provided by Tap
					id:
						paymentGatewayType === PAYMENT_GATEWAY_TYPE.TAP_PAYMENT_UAE
							? import.meta.env.VITE_TAP_PAYMENT_UAE_MERCHANT_ID
							: import.meta.env.VITE_TAP_PAYMENT_MERCHANT_ID
				}}
				features={{
					supportsCouponCode: false
				}}
				transaction={{
					// The amount to be charged
					amount: watch('total'),
					// The currency of the amount
					currency: Currency
				}}
				// The scope of the SDK and it can be one of these scopes:
				// [TapToken,AppleToken], by default it is TapToken)
				scope={Scope.TapToken}
				acceptance={{
					// The supported networks for the Apple Pay button and it
					// can be one of these networks: [Mada,Visa,MasterCard], by default
					// we bring all the supported networks from tap merchant configuration
					supportedBrands:
						paymentGatewayType === PAYMENT_GATEWAY_TYPE.TAP_PAYMENT_UAE
							? [SupportedNetworks.Visa, SupportedNetworks.MasterCard]
							: [SupportedNetworks.Mada, SupportedNetworks.Visa, SupportedNetworks.MasterCard]
				}}
				// The billing contact information
				customer={{
					name: [
						{
							// "en or ar",
							lang: LangCountryCode?.split('_').shift() === 'en' ? Locale.EN : Locale.AR,
							// "First name of the customer.",
							first: fullName?.split(' ')[0],
							// "Last name of the customer.",
							last: fullName?.split(' ').slice(1).join(' ')
						}
					],
					// Defines the contact details for the customer & to be used in creating the billing contact info in Apple pay request
					contact: {
						// "The customer's email",
						email,
						// "The customer's phone number"
						phone: {
							// "The customer's country code",
							countryCode: userPhone.countryCode,
							// "The customer's phone number
							number: userPhone.phone
						}
					}
				}}
				// for styling button
				interface={{
					// The locale of the Apple Pay button and it can be one of these locales:[EN,AR]
					locale: LangCountryCode?.split('_').shift() === 'en' ? Locale.EN : Locale.AR,
					// The theme of the Apple Pay button and it can be one of
					// these values : [light,Dark], by default it is detected from user device
					theme: darkMode ? ThemeMode.LIGHT : ThemeMode.DARK,
					// The type of the Apple Pay
					type: isSubscription ? ButtonType.SUBSCRIBE : ButtonType.PAY,
					// The border of the Apple Pay button and it can be one of these values:[curved,straight]
					edges: Edges.CURVED
				}}
				// optional (A callback function that will be called when you cancel
				// the payment process)
				onCancel={() => {
					AppLogger.log('Apple Pay payment process has been canceled by the user.');
				}}
				// optional (A callback function that will be called when you have an error)
				onError={(err) => {
					AppLogger.error(err);
					showError();
				}}
				// optional (A async function that will be called after creating the token
				// successfully)
				onSuccess={async (token) => {
					// do your stuff here...
					AppLogger.log(token);
					completeCreditPayment(token);
				}}
				onReady={() => {
					AppLogger.log('Apple Pay button is ready');
				}}
			/>
		</div>
	);
};

export default TapApplePayButton;
